import React, { useCallback } from "react";
import { FirestickConfig } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label, Textarea } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { observer } from "mobx-react";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { useDisclosure } from "react-use-disclosure";
import { FileUploadModal } from "../../common/FileUploadModal";

type Props = {
  firestickConfig?: FirestickConfig;
  isSubmitting: boolean;
  onSubmit: (values: FirestickConfig) => void;
};

const FirestickConfigForm: React.FC<Props> = observer(
  ({ firestickConfig, isSubmitting, onSubmit }) => {
    const { t } = useTranslation();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();

    const formik = useFormik({
      initialValues: {
        id: firestickConfig?.id || undefined,
        categoryId: firestickConfig?.categoryId || 0,
        category: firestickConfig?.category || "",
        logoImg: firestickConfig?.logoImg || "",
        streamingImg: firestickConfig?.streamingImg || "",
        description: firestickConfig?.description || "",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        category: Yup.string().required(
          t("components.common.firestickConfigForm.categoryRequired")
        ),
        logoImg: Yup.string().required(
          t("components.common.firestickConfigForm.logoImgRequired")
        ),
        streamingImg: Yup.string().required(
          t("components.common.firestickConfigForm.streamingImgRequired")
        ),
        description: Yup.string().required(
          t("components.common.firestickConfigForm.descriptionRequired")
        ),
      }),
    });

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("streamingImg", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    return (
      <Container paddingBottom={"10rem"}>
        <form onSubmit={formik.handleSubmit}>
          <Container pb={"20px"}>
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="streamingImg">
                    {t("components.common.firestickConfigForm.streamingImg")}
                  </Label>
                  {formik.values.streamingImg.length > 0 ? (
                    <img
                      src={formik.values.streamingImg}
                      alt={formik.values.streamingImg}
                    />
                  ) : (
                    <p>
                      {t(
                        "components.common.firestickConfigForm.streamingImgPlaceholder"
                      )}
                    </p>
                  )}
                  {formik.errors.streamingImg && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("streamingImg").error}
                    </Feedback>
                  )}
                  <Button color="light" onClick={openModalUpload}>
                    {t("common.upload")}
                  </Button>
                </FormControl>
              </Col>
            </Row>
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="logoImg">
                    {t("components.common.firestickConfigForm.logoImg")}
                  </Label>
                  {formik.values.logoImg.length > 0 && (
                    <img
                      src={formik.values.logoImg}
                      alt={formik.values.logoImg}
                      style={{
                        maxWidth: "300px",
                        height: "auto",
                        margin: "10px 0",
                      }}
                    />
                  )}
                  <Input
                    type="text"
                    id="logoImg"
                    name="logoImg"
                    placeholder={t(
                      "components.common.firestickConfigForm.logoImgPlaceholder"
                    )}
                    value={formik.values.logoImg}
                    onChange={formik.handleChange}
                    state={formik.errors.logoImg ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("logoImg").error}
                  />
                </FormControl>
              </Col>
            </Row>

            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="category">
                    {t("components.common.firestickConfigForm.category")}
                  </Label>
                  <Input
                    type="text"
                    id="category"
                    name="category"
                    placeholder={t(
                      "components.common.firestickConfigForm.categoryPlaceholder"
                    )}
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    state={formik.errors.category ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("category").error}
                  />
                </FormControl>
              </Col>
            </Row>

            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="description">
                    {t("components.common.firestickConfigForm.description")}
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder={t(
                      "components.common.firestickConfigForm.descriptionPlaceholder"
                    )}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    showState={true}
                  />
                </FormControl>
              </Col>
            </Row>
          </Container>

          <Row>
            <Col textAlign="center">
              <Button type={"submit"} disabled={isSubmitting}>
                {t("common.save")}
              </Button>
            </Col>
          </Row>

          <FileUploadModal
            isOpen={isModalUploadOpen}
            onClose={closeModalUpload}
            onFileUploaded={handleUploadPoster}
            maxFiles={1}
            accept="image/jpeg, image/png"
          />
        </form>
      </Container>
    );
  }
);

export default FirestickConfigForm;
